import {
    Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy
} from '@angular/core';
import { DomSanitizer, SafeUrl, SafeStyle } from '@angular/platform-browser';
import { ImageCropperExifService } from './image-cropper-exif.service';

interface MoveStart {
    active: boolean;
    type: string | null;
    position: string | null;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    clientX: number;
    clientY: number;
}

interface Dimensions {
    width: number;
    height: number;
}

export interface CropperPosition {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}
export class ImageCropperComponent implements OnChanges {
private originalImage: any;
private moveStart: MoveStart;
private maxSize: Dimensions;
private originalSize: Dimensions;

    safeImgDataUrl: SafeUrl | string;
    marginLeft: SafeStyle | string = '0px';
    imageVisible = false;
/**
 * @param {?} event
 * @return {?}
 */
set imageChangedEvent(event: any) {
        this.initCropper();
        if (event && event.target && event.target.files && event.target.files.length > 0) {
            this.loadImage(event);
        }
    }
/**
 * @param {?} imageBase64
 * @return {?}
 */
set imageBase64(imageBase64: string) {
        this.initCropper();
        this.loadBase64Image(imageBase64);
    }

     format: 'png' | 'jpeg' | 'bmp' | 'webp' | 'ico' = 'png';
     maintainAspectRatio = true;
     aspectRatio = 1;
     resizeToWidth = 0;
     onlyScaleDown = false;
     imageQuality = 92;
     cropper: CropperPosition = {
        x1: -100,
        y1: -100,
        x2: 10000,
        y2: 10000
    };

     imageCropped = new EventEmitter<string>();
     imageLoaded = new EventEmitter<void>();
     loadImageFailed = new EventEmitter<void>();
/**
 * @param {?} elementRef
 * @param {?} sanitizer
 * @param {?} cd
 * @param {?} exifService
 */
constructor(
private elementRef: ElementRef,
private sanitizer: DomSanitizer,
private cd: ChangeDetectorRef,
private exifService: ImageCropperExifService
    ) {
        this.initCropper();
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
        if (changes['cropper']) {
            setTimeout(() => {
                this.setMaxSize();
                this.checkCropperPosition(false);
                this.crop();
                this.cd.markForCheck();
            });
        }
    }
/**
 * @return {?}
 */
private initCropper() {
        this.imageVisible = false;
        this.originalImage = null;
        this.safeImgDataUrl = 'data:image/png;base64,iVBORw0KGg'
            + 'oAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQYV2NgAAIAAAU'
            + 'AAarVyFEAAAAASUVORK5CYII=';
        this.moveStart = {
            active: false,
            type: null,
            position: null,
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 0,
            clientX: 0,
            clientY: 0
        };
        this.maxSize = {
            width: 0,
            height: 0
        };
        this.originalSize = {
            width: 0,
            height: 0
        };
        this.cropper.x1 = -100;
        this.cropper.y1 = -100;
        this.cropper.x2 = 10000;
        this.cropper.y2 = 10000;
    }
/**
 * @param {?} event
 * @return {?}
 */
loadImage(event: any) {
        const /** @type {?} */ file: File = event.target.files[0];

        // Check the file type to see if a match is found.
        // If a match is found then the file uploaded is a valid image type
        if (/(jpe?g|png|gif)/i.test(file.type) === false) {
            this.loadImageFailed.emit();
            return;
        }

        let /** @type {?} */ fileReader: FileReader = new FileReader();
        fileReader.onloadend = (loadEvent: any) => {
            const /** @type {?} */ image = loadEvent.target.result;
            this.exifService.getOrientedImage(image, file.type).subscribe(
                (modifiedImage: HTMLImageElement) => {
                    this.loadBase64Image(modifiedImage.src);
                },
                (error: any) => {
                    this.loadImageFailed.emit();
                }
            );
        };
        fileReader.readAsArrayBuffer(file);
    }
/**
 * @param {?} imageBase64
 * @return {?}
 */
private loadBase64Image(imageBase64: string) {
        this.originalImage = new Image();
        this.originalImage.onload = () => {
            this.originalSize.width = this.originalImage.width;
            this.originalSize.height = this.originalImage.height;
            this.cd.markForCheck();
        };
        this.safeImgDataUrl = this.sanitizer.bypassSecurityTrustResourceUrl(imageBase64);
        this.originalImage.src = imageBase64;
    }
/**
 * @return {?}
 */
imageLoadedInView(): void {
        if (this.originalImage != null) {
            this.imageLoaded.emit();
            setTimeout(() => {
                this.setMaxSize();
                this.resetCropperPosition();
                this.cd.markForCheck();
            });
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
imageResizedInView(event: Event) {
        this.resizeCropperPosition();
        this.setMaxSize();
    }
/**
 * @return {?}
 */
private resizeCropperPosition() {
        const /** @type {?} */ displayedImage = this.elementRef.nativeElement.querySelector('.source-image');
        if (this.maxSize.width !== displayedImage.offsetWidth || this.maxSize.height !== displayedImage.offsetHeight) {
            this.cropper.x1 = this.cropper.x1 * displayedImage.offsetWidth / this.maxSize.width;
            this.cropper.x2 = this.cropper.x2 * displayedImage.offsetWidth / this.maxSize.width;
            this.cropper.y1 = this.cropper.y1 * displayedImage.offsetHeight / this.maxSize.height;
            this.cropper.y2 = this.cropper.y2 * displayedImage.offsetHeight / this.maxSize.height;
        }
    }
/**
 * @return {?}
 */
private resetCropperPosition() {
        const /** @type {?} */ displayedImage = this.elementRef.nativeElement.querySelector('.source-image');
        if (displayedImage.offsetWidth / this.aspectRatio < displayedImage.offsetHeight) {
            this.cropper.x1 = 0;
            this.cropper.x2 = displayedImage.offsetWidth;
            const /** @type {?} */ cropperHeight = displayedImage.offsetWidth / this.aspectRatio;
            this.cropper.y1 = (displayedImage.offsetHeight - cropperHeight) / 2;
            this.cropper.y2 = this.cropper.y1 + cropperHeight;
        } else {
            this.cropper.y1 = 0;
            this.cropper.y2 = displayedImage.offsetHeight;
            const /** @type {?} */ cropperWidth = displayedImage.offsetHeight * this.aspectRatio;
            this.cropper.x1 = (displayedImage.offsetWidth - cropperWidth) / 2;
            this.cropper.x2 = this.cropper.x1 + cropperWidth;
        }

        this.crop();
        this.imageVisible = true;
    }
/**
 * @param {?} event
 * @param {?} moveType
 * @param {?=} position
 * @return {?}
 */
startMove(event: any, moveType: string, position: string | null = null) {
        this.moveStart.active = true;
        this.moveStart.type = moveType;
        this.moveStart.position = position;
        this.moveStart.clientX = this.getClientX(event);
        this.moveStart.clientY = this.getClientY(event);
        Object.assign(this.moveStart, this.cropper);
        this.cd.markForCheck();
    }
/**
 * @param {?} event
 * @return {?}
 */
moveImg(event: any) {
        if (this.moveStart.active) {
            event.stopPropagation();
            event.preventDefault();
            this.setMaxSize();
            if (this.moveStart.type === 'move') {
                this.move(event);
                this.checkCropperPosition(true);
            } else if (this.moveStart.type === 'resize') {
                this.resize(event);
                this.checkCropperPosition(false);
            }
            this.cd.markForCheck();
        }
    }
/**
 * @return {?}
 */
private setMaxSize() {
        const /** @type {?} */ el = this.elementRef.nativeElement.querySelector('.source-image');
        this.maxSize.width = el.offsetWidth;
        this.maxSize.height = el.offsetHeight;
        this.marginLeft = this.sanitizer.bypassSecurityTrustStyle('calc(50% - ' + this.maxSize.width / 2 + 'px)');
    }
/**
 * @param {?=} maintainSize
 * @return {?}
 */
private checkCropperPosition(maintainSize = false) {
        if (this.cropper.x1 < 0) {
            this.cropper.x2 -= maintainSize ? this.cropper.x1 : 0;
            this.cropper.x1 = 0;
        }
        if (this.cropper.y1 < 0) {
            this.cropper.y2 -= maintainSize ? this.cropper.y1 : 0;
            this.cropper.y1 = 0;
        }
        if (this.cropper.x2 > this.maxSize.width) {
            this.cropper.x1 -= maintainSize ? (this.cropper.x2 - this.maxSize.width) : 0;
            this.cropper.x2 = this.maxSize.width;
        }
        if (this.cropper.y2 > this.maxSize.height) {
            this.cropper.y1 -= maintainSize ? (this.cropper.y2 - this.maxSize.height) : 0;
            this.cropper.y2 = this.maxSize.height;
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
moveStop(event: any) {
        if (this.moveStart.active) {
            this.moveStart.active = false;
            this.crop();
            this.cd.markForCheck();
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
private move(event: any) {
        const /** @type {?} */ diffX = this.getClientX(event) - this.moveStart.clientX;
        const /** @type {?} */ diffY = this.getClientY(event) - this.moveStart.clientY;

        this.cropper.x1 = this.moveStart.x1 + diffX;
        this.cropper.y1 = this.moveStart.y1 + diffY;
        this.cropper.x2 = this.moveStart.x2 + diffX;
        this.cropper.y2 = this.moveStart.y2 + diffY;
    }
/**
 * @param {?} event
 * @return {?}
 */
private resize(event: any) {
        const /** @type {?} */ diffX = this.getClientX(event) - this.moveStart.clientX;
        const /** @type {?} */ diffY = this.getClientY(event) - this.moveStart.clientY;
        switch (this.moveStart.position) {
            case 'left':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
                break;
            case 'topleft':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
                break;
            case 'top':
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
                break;
            case 'topright':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
                this.cropper.y1 = Math.min(this.moveStart.y1 + diffY, this.cropper.y2 - 20);
                break;
            case 'right':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
                break;
            case 'bottomright':
                this.cropper.x2 = Math.max(this.moveStart.x2 + diffX, this.cropper.x1 + 20);
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
                break;
            case 'bottom':
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
                break;
            case 'bottomleft':
                this.cropper.x1 = Math.min(this.moveStart.x1 + diffX, this.cropper.x2 - 20);
                this.cropper.y2 = Math.max(this.moveStart.y2 + diffY, this.cropper.y1 + 20);
                break;
        }

        if (this.maintainAspectRatio) {
            this.checkAspectRatio();
        }
    }
/**
 * @return {?}
 */
private checkAspectRatio() {
        let /** @type {?} */ overflowX = 0;
        let /** @type {?} */ overflowY = 0;

        switch (this.moveStart.position) {
            case 'top':
                this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'bottom':
                this.cropper.x2 = this.cropper.x1 + (this.cropper.y2 - this.cropper.y1) * this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : (overflowX / this.aspectRatio);
                }
                break;
            case 'topleft':
                this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(0 - this.cropper.x1, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x1 += (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'topright':
                this.cropper.y1 = this.cropper.y2 - (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(0 - this.cropper.y1, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y1 += (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'right':
            case 'bottomright':
                this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(this.cropper.x2 - this.maxSize.width, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x2 -= (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
            case 'left':
            case 'bottomleft':
                this.cropper.y2 = this.cropper.y1 + (this.cropper.x2 - this.cropper.x1) / this.aspectRatio;
                overflowX = Math.max(0 - this.cropper.x1, 0);
                overflowY = Math.max(this.cropper.y2 - this.maxSize.height, 0);
                if (overflowX > 0 || overflowY > 0) {
                    this.cropper.x1 += (overflowY * this.aspectRatio) > overflowX ? (overflowY * this.aspectRatio) : overflowX;
                    this.cropper.y2 -= (overflowY * this.aspectRatio) > overflowX ? overflowY : overflowX / this.aspectRatio;
                }
                break;
        }
    }
/**
 * @return {?}
 */
private crop() {
        const /** @type {?} */ displayedImage = this.elementRef.nativeElement.querySelector('.source-image');
        if (displayedImage && this.originalImage != null) {
            const /** @type {?} */ ratio  = this.calculateRatio(this.originalImage, displayedImage);
            const /** @type {?} */ left   = Math.round(this.cropper.x1 * ratio);
            const /** @type {?} */ top    = Math.round(this.cropper.y1 * ratio);
            const /** @type {?} */ width  = Math.round((this.cropper.x2 - this.cropper.x1) * ratio);
            const /** @type {?} */ height = Math.round((this.cropper.y2 - this.cropper.y1) * ratio);

            const /** @type {?} */ resizeRatio   = this.getResizeRatio(width);
            const /** @type {?} */ resizedWidth  = width * resizeRatio;
            const /** @type {?} */ resizedHeight = height * resizeRatio;

            const /** @type {?} */ cropCanvas  = /** @type {?} */(( document.createElement('canvas') as HTMLCanvasElement));
            cropCanvas.width  = resizedWidth;
            cropCanvas.height = resizedHeight;

            const /** @type {?} */ context = cropCanvas.getContext('2d');
            if (context) {
                // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
                // drawImage(
                //     image,
                //     source x,
                //     source y,
                //     source width,
                //     source height,
                //     destination x,
                //     destination y,
                //     destination width,
                //     destination height
                // )
                context.drawImage(this.originalImage, left, top, width, height, 0, 0, resizedWidth, resizedHeight);

                const /** @type {?} */ quality      = Math.min(1, Math.max(0, this.imageQuality / 100));
                const /** @type {?} */ croppedImage = cropCanvas.toDataURL(`image/${this.format}`, quality);

                if (croppedImage.length > 10) {
                    this.imageCropped.emit(croppedImage);
                }
            }
        }
    }
/**
 * @param {?} width
 * @return {?}
 */
private getResizeRatio(width: number): number {
        return this.resizeToWidth > 0 && (!this.onlyScaleDown || width > this.resizeToWidth)
            ? this.resizeToWidth / width
            : 1;
    }
/**
 * @param {?} event
 * @return {?}
 */
private getClientX(event: any) {
        return event.clientX != null ? event.clientX : event.touches[0].clientX;
    }
/**
 * @param {?} event
 * @return {?}
 */
private getClientY(event: any) {
        return event.clientY != null ? event.clientY : event.touches[0].clientY;
    }
/**
 * Calculate the ratio to use when determining the proper
 * cropping coordinates the user has chosen
 * 
 * @param {?} workingImage The image we are currently working with. This may be the original
 *                     image the user took or a rotated version of that image
 * @param {?} displayedImage Image that is currently set in the img component in the HTML view
 * @return {?}
 */
private calculateRatio(workingImage: HTMLImageElement, displayedImage: HTMLImageElement): number {
        return workingImage.width / displayedImage.offsetWidth;
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'image-cropper',
    template: `
      <div>
          <img
              [src]="safeImgDataUrl"
              [style.visibility]="imageVisible ? 'visible' : 'hidden'"
              (load)="imageLoadedInView()"
              class="source-image"
          />
          <div class="cropper"
               [style.top.px]="cropper.y1"
               [style.left.px]="cropper.x1"
               [style.width.px]="cropper.x2 - cropper.x1"
               [style.height.px]="cropper.y2 - cropper.y1"
               [style.margin-left]="marginLeft"
               [style.visibility]="imageVisible ? 'visible' : 'hidden'"
          >
              <div
                  (mousedown)="startMove($event, 'move')"
                  (touchstart)="startMove($event, 'move')"
                  class="move"
              >&nbsp;</div>
              <span
                  class="resize topleft"
                  (mousedown)="startMove($event, 'resize', 'topleft')"
                  (touchstart)="startMove($event, 'resize', 'topleft')"
              ><span class="square"></span></span>
              <span
                  class="resize top"
              ><span class="square"></span></span>
              <span
                  class="resize topright"
                  (mousedown)="startMove($event, 'resize', 'topright')"
                  (touchstart)="startMove($event, 'resize', 'topright')"
              ><span class="square"></span></span>
              <span
                  class="resize right"
              ><span class="square"></span></span>
              <span
                  class="resize bottomright"
                  (mousedown)="startMove($event, 'resize', 'bottomright')"
                  (touchstart)="startMove($event, 'resize', 'bottomright')"
              ><span class="square"></span></span>
              <span
                  class="resize bottom"
              ><span class="square"></span></span>
              <span
                  class="resize bottomleft"
                  (mousedown)="startMove($event, 'resize', 'bottomleft')"
                  (touchstart)="startMove($event, 'resize', 'bottomleft')"
              ><span class="square"></span></span>
              <span
                  class="resize left"
              ><span class="square"></span></span>
              <span
                  class="resize-bar top"
                  (mousedown)="startMove($event, 'resize', 'top')"
                  (touchstart)="startMove($event, 'resize', 'top')"
              ></span>
              <span
                  class="resize-bar right"
                  (mousedown)="startMove($event, 'resize', 'right')"
                  (touchstart)="startMove($event, 'resize', 'right')"
              ></span>
              <span
                  class="resize-bar bottom"
                  (mousedown)="startMove($event, 'resize', 'bottom')"
                  (touchstart)="startMove($event, 'resize', 'bottom')"
              ></span>
              <span
                  class="resize-bar left"
                  (mousedown)="startMove($event, 'resize', 'left')"
                  (touchstart)="startMove($event, 'resize', 'left')"
              ></span>
          </div>
      </div>
    `,
    styles: [`
      :host {
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        position: relative;
        width: 100%;
        max-width: 100%;
        max-height: 100%;
        overflow: hidden;
        padding: 5px;
        text-align: center;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none; }
        :host > div {
          position: relative;
          width: 100%; }
          :host > div .source-image {
            max-width: 100%;
            max-height: 100%; }
        :host .cropper {
          position: absolute;
          display: -webkit-box;
          display: -ms-flexbox;
          display: flex;
          color: #53535C !important;
          background: transparent !important;
          outline-color: rgba(255, 255, 255, 0.3);
          outline-width: 1000px;
          outline-style: solid;
          -ms-touch-action: none;
              touch-action: none; }
          :host .cropper:after {
            position: absolute;
            content: '';
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            pointer-events: none;
            border: dashed 1px;
            opacity: .75;
            color: inherit;
            z-index: 1; }
          :host .cropper .move {
            width: 100%;
            cursor: move;
            border: 1px solid rgba(255, 255, 255, 0.5); }
          :host .cropper .resize {
            position: absolute;
            display: inline-block;
            line-height: 6px;
            padding: 8px;
            opacity: .85;
            z-index: 1; }
            :host .cropper .resize .square {
              display: inline-block;
              background: #53535C !important;
              width: 6px;
              height: 6px;
              border: 1px solid rgba(255, 255, 255, 0.5); }
            :host .cropper .resize.topleft {
              top: -12px;
              left: -12px;
              cursor: nw-resize; }
            :host .cropper .resize.top {
              top: -12px;
              left: calc(50% - 12px);
              cursor: n-resize; }
            :host .cropper .resize.topright {
              top: -12px;
              right: -12px;
              cursor: ne-resize; }
            :host .cropper .resize.right {
              top: calc(50% - 12px);
              right: -12px;
              cursor: e-resize; }
            :host .cropper .resize.bottomright {
              bottom: -12px;
              right: -12px;
              cursor: se-resize; }
            :host .cropper .resize.bottom {
              bottom: -12px;
              left: calc(50% - 12px);
              cursor: s-resize; }
            :host .cropper .resize.bottomleft {
              bottom: -12px;
              left: -12px;
              cursor: sw-resize; }
            :host .cropper .resize.left {
              top: calc(50% - 12px);
              left: -12px;
              cursor: w-resize; }
          :host .cropper .resize-bar {
            position: absolute;
            z-index: 1; }
            :host .cropper .resize-bar.top {
              top: -11px;
              left: 11px;
              width: calc(100% - 22px);
              height: 22px;
              cursor: n-resize; }
            :host .cropper .resize-bar.right {
              top: 11px;
              right: -11px;
              height: calc(100% - 22px);
              width: 22px;
              cursor: e-resize; }
            :host .cropper .resize-bar.bottom {
              bottom: -11px;
              left: 11px;
              width: calc(100% - 22px);
              height: 22px;
              cursor: s-resize; }
            :host .cropper .resize-bar.left {
              top: 11px;
              left: -11px;
              height: calc(100% - 22px);
              width: 22px;
              cursor: w-resize; }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: DomSanitizer, },
{type: ChangeDetectorRef, },
{type: ImageCropperExifService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'imageChangedEvent': [{ type: Input },],
'imageBase64': [{ type: Input },],
'format': [{ type: Input },],
'maintainAspectRatio': [{ type: Input },],
'aspectRatio': [{ type: Input },],
'resizeToWidth': [{ type: Input },],
'onlyScaleDown': [{ type: Input },],
'imageQuality': [{ type: Input },],
'cropper': [{ type: Input },],
'imageCropped': [{ type: Output },],
'imageLoaded': [{ type: Output },],
'loadImageFailed': [{ type: Output },],
'imageResizedInView': [{ type: HostListener, args: ['window:resize', ['$event'], ] },],
'moveImg': [{ type: HostListener, args: ['document:mousemove', ['$event'], ] },{ type: HostListener, args: ['document:touchmove', ['$event'], ] },],
'moveStop': [{ type: HostListener, args: ['document:mouseup', ['$event'], ] },{ type: HostListener, args: ['document:touchend', ['$event'], ] },],
};
}

function ImageCropperComponent_tsickle_Closure_declarations() {
/** @type {?} */
ImageCropperComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
ImageCropperComponent.ctorParameters;
/** @type {?} */
ImageCropperComponent.propDecorators;
/** @type {?} */
ImageCropperComponent.prototype.originalImage;
/** @type {?} */
ImageCropperComponent.prototype.moveStart;
/** @type {?} */
ImageCropperComponent.prototype.maxSize;
/** @type {?} */
ImageCropperComponent.prototype.originalSize;
/** @type {?} */
ImageCropperComponent.prototype.safeImgDataUrl;
/** @type {?} */
ImageCropperComponent.prototype.marginLeft;
/** @type {?} */
ImageCropperComponent.prototype.imageVisible;
/** @type {?} */
ImageCropperComponent.prototype.format;
/** @type {?} */
ImageCropperComponent.prototype.maintainAspectRatio;
/** @type {?} */
ImageCropperComponent.prototype.aspectRatio;
/** @type {?} */
ImageCropperComponent.prototype.resizeToWidth;
/** @type {?} */
ImageCropperComponent.prototype.onlyScaleDown;
/** @type {?} */
ImageCropperComponent.prototype.imageQuality;
/** @type {?} */
ImageCropperComponent.prototype.cropper;
/** @type {?} */
ImageCropperComponent.prototype.imageCropped;
/** @type {?} */
ImageCropperComponent.prototype.imageLoaded;
/** @type {?} */
ImageCropperComponent.prototype.loadImageFailed;
/** @type {?} */
ImageCropperComponent.prototype.elementRef;
/** @type {?} */
ImageCropperComponent.prototype.sanitizer;
/** @type {?} */
ImageCropperComponent.prototype.cd;
/** @type {?} */
ImageCropperComponent.prototype.exifService;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
